/**
 * WeaveIn.ai Platform - Medicaid Enrollment Detail Modal
 * Displays detailed information about a Medicaid enrollment
 */

import React from 'react';
import {
  X,
  User,
  MapPin,
  Users,
  DollarSign,
  Briefcase,
  Calendar,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { MedicaidEnrollment, EnrollmentStatus, WorkRequirementStatus } from '@/services/api/medicaidEnrollment';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';

interface MedicaidEnrollmentDetailModalProps {
  open: boolean;
  enrollment: MedicaidEnrollment | null;
  onClose: () => void;
}

const getStatusBadgeVariant = (status: EnrollmentStatus): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'ACTIVELY_ENROLLED':
    case 'REACTIVATED':
      return 'default';
    case 'INITIAL_APPLICATION':
    case 'PENDING_VERIFICATION':
    case 'PENDING_REDETERMINATION':
      return 'secondary';
    case 'SUSPENDED':
    case 'TERMINATED':
      return 'destructive';
    default:
      return 'outline';
  }
};

const getStatusLabel = (status: EnrollmentStatus): string => {
  const labels: Record<EnrollmentStatus, string> = {
    'INITIAL_APPLICATION': 'Initial Application',
    'PENDING_VERIFICATION': 'Pending Verification',
    'ACTIVELY_ENROLLED': 'Actively Enrolled',
    'PENDING_REDETERMINATION': 'Pending Redetermination',
    'SUSPENDED': 'Suspended',
    'TERMINATED': 'Terminated',
    'REACTIVATED': 'Reactivated',
    'COMPLETED': 'Completed',
  };
  return labels[status] || status;
};

const getWorkStatusLabel = (status?: WorkRequirementStatus): string => {
  if (!status) return 'N/A';
  const labels: Record<WorkRequirementStatus, string> = {
    'EXEMPT': 'Exempt',
    'REQUIRED': 'Required',
    'COMPLIANT': 'Compliant',
    'NON_COMPLIANT': 'Non-Compliant',
    'PENDING_VERIFICATION': 'Pending Verification',
  };
  return labels[status] || status;
};

// formatDate is now handled by fmt.long() from useFormattedDate hook inside the component

const formatCurrency = (amount?: number): string => {
  if (amount === undefined || amount === null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

export default function MedicaidEnrollmentDetailModal({
  open,
  enrollment,
  onClose,
}: MedicaidEnrollmentDetailModalProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();

  if (!enrollment) return null;

  const StatusIcon = enrollment.status === 'ACTIVELY_ENROLLED' || enrollment.status === 'REACTIVATED'
    ? CheckCircle
    : enrollment.status === 'SUSPENDED' || enrollment.status === 'TERMINATED'
    ? AlertCircle
    : Clock;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {t('medicaid.enrollment_details', 'Enrollment Details')}
            </DialogTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Status Header */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground">
                {t('medicaid.enrollment_number', 'Enrollment Number')}
              </p>
              <p className="text-lg font-semibold">{enrollment.enrollment_number}</p>
            </div>
            <div className="flex items-center gap-2">
              <StatusIcon className={`h-5 w-5 ${
                enrollment.status === 'ACTIVELY_ENROLLED' || enrollment.status === 'REACTIVATED'
                  ? 'text-green-500'
                  : enrollment.status === 'SUSPENDED' || enrollment.status === 'TERMINATED'
                  ? 'text-red-500'
                  : 'text-yellow-500'
              }`} />
              <Badge variant={getStatusBadgeVariant(enrollment.status)}>
                {getStatusLabel(enrollment.status)}
              </Badge>
            </div>
          </div>

          {/* Member Information */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <User className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-semibold">
                {t('medicaid.member_information', 'Member Information')}
              </h3>
            </div>
            <Separator className="mb-3" />
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">{t('common.name', 'Name')}</p>
                <p className="font-medium">
                  {enrollment.member_info?.first_name} {enrollment.member_info?.last_name}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">{t('common.dob', 'Date of Birth')}</p>
                <p className="font-medium">{enrollment.member_info?.date_of_birth ? fmt.long(enrollment.member_info.date_of_birth) : '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t('common.phone', 'Phone')}</p>
                <p className="font-medium">{enrollment.member_info?.phone_number || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t('common.email', 'Email')}</p>
                <p className="font-medium">{enrollment.member_info?.email || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t('common.language', 'Preferred Language')}</p>
                <p className="font-medium">{enrollment.member_info?.preferred_language || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t('medicaid.state', 'State')}</p>
                <p className="font-medium">{enrollment.state_code}</p>
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-semibold">
                {t('medicaid.address', 'Address')}
              </h3>
            </div>
            <Separator className="mb-3" />
            <div className="text-sm">
              <p className="font-medium">{enrollment.address?.street_address || '-'}</p>
              <p className="font-medium">
                {enrollment.address?.city}, {enrollment.address?.state} {enrollment.address?.zip_code}
              </p>
              {enrollment.address?.county && (
                <p className="text-muted-foreground">{enrollment.address.county} County</p>
              )}
            </div>
          </div>

          {/* Household Information */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Users className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-semibold">
                {t('medicaid.household', 'Household Information')}
              </h3>
            </div>
            <Separator className="mb-3" />
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">{t('medicaid.household_size', 'Household Size')}</p>
                <p className="font-medium">{enrollment.household?.household_size || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t('medicaid.dependents', 'Number of Dependents')}</p>
                <p className="font-medium">{enrollment.household?.number_of_dependents ?? '-'}</p>
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-semibold">
                {t('medicaid.financial', 'Financial Information')}
              </h3>
            </div>
            <Separator className="mb-3" />
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">{t('medicaid.monthly_income', 'Monthly Income')}</p>
                <p className="font-medium">{formatCurrency(enrollment.financial?.monthly_income)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t('medicaid.total_assets', 'Total Assets')}</p>
                <p className="font-medium">{formatCurrency(enrollment.financial?.total_assets)}</p>
              </div>
              {enrollment.financial?.income_sources && enrollment.financial.income_sources.length > 0 && (
                <div className="col-span-2">
                  <p className="text-muted-foreground">{t('medicaid.income_sources', 'Income Sources')}</p>
                  <p className="font-medium">{enrollment.financial.income_sources.join(', ')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Work Requirements */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Briefcase className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-semibold">
                {t('medicaid.work_requirements', 'Work Requirements')}
              </h3>
            </div>
            <Separator className="mb-3" />
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">{t('medicaid.work_status', 'Work Requirement Status')}</p>
                <p className="font-medium">{getWorkStatusLabel(enrollment.work_requirement_status)}</p>
              </div>
              {enrollment.work_requirement?.is_exempt && (
                <div>
                  <p className="text-muted-foreground">{t('medicaid.exemption_reason', 'Exemption Reason')}</p>
                  <p className="font-medium">{enrollment.work_requirement.exemption_reason || '-'}</p>
                </div>
              )}
            </div>
          </div>

          {/* Important Dates */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-semibold">
                {t('medicaid.important_dates', 'Important Dates')}
              </h3>
            </div>
            <Separator className="mb-3" />
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">{t('medicaid.application_date', 'Application Date')}</p>
                <p className="font-medium">{enrollment.application_date ? fmt.long(enrollment.application_date) : '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t('medicaid.approval_date', 'Approval Date')}</p>
                <p className="font-medium">{enrollment.approval_date ? fmt.long(enrollment.approval_date) : '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t('medicaid.last_redetermination', 'Last Redetermination')}</p>
                <p className="font-medium">{enrollment.last_redetermination_date ? fmt.long(enrollment.last_redetermination_date) : '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">{t('medicaid.next_redetermination', 'Next Redetermination')}</p>
                <p className="font-medium">{enrollment.next_redetermination_date ? fmt.long(enrollment.next_redetermination_date) : '-'}</p>
              </div>
            </div>
          </div>

          {/* Notes */}
          {enrollment.notes && (
            <div>
              <h3 className="font-semibold mb-2">{t('common.notes', 'Notes')}</h3>
              <Separator className="mb-3" />
              <p className="text-sm text-muted-foreground">{enrollment.notes}</p>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            {t('common.close', 'Close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
